//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  TenderFailuresGlobalStyle,
} from './TenderFailures.styles';

const hero = {
  slug: 'tender-failures',
  title: 'Tender Failures',
  description:
    'When a carrier rejects a tender or lets it time out, the agent re-tenders down your routing guide and re-books the next eligible carrier — inside a cost ceiling that stops the fallback from costing more than the miss.',
  metric: '8–10%',
  metricLabel: 'overpayment on flat rates eliminated',
  // metricSource: 'MEASURED AT RED GOLD',
};

const watchItRun = {
  title: 'A tender just fell through',
  stageHeadTitle:
    'A carrier just rejected the tender on MEM → ORD, picking up tomorrow.',
  stageHeadMeta: 'Procurement · #LS-49010',
  rows: [
    {
      time: '17:02:01',
      text: 'Tender rejected on MEM → ORD — pickup tomorrow 6am.',
    },
    {
      time: '17:02:03',
      text: 'Re-tendered to the next carrier on your routing guide.',
    },
    {
      time: '17:02:44',
      text: 'No response in your 40-min window — moved to the third.',
    },
    {
      time: '17:03:10',
      text: 'Covered at contract. Rate con out, dock appointment held.',
    },
    {
      time: '17:03:12',
      text: 'Re-covered before the miss reached your team.',
    },
  ],
  summaryText:
    'A carrier fell through on a next-day load. Loadsmart AI worked down your routing guide, re-tendered until it re-covered at contract, and kept the dock appointment — a rejected tender never became a scramble on your desk.',
  agentBadge: 'Procurement Agent',
  escalationTitle: 'Every fallback has a cost ceiling.',
  escalationText:
    'Automating a fallback only helps if the fallback doesn’t cost more than the miss did. Every fallback rate is capped at a set ceiling above the anchor rate and checked against live market benchmarks before it books. Your ranking holds too: a carrier you already contracted beats a slightly cheaper one further down the guide.',
};

const howItWorks = {
  stepsTitleWhite: 'Works your routing guide in seconds,',
  stepsTitleGradient: 'inside a cost ceiling.',
  steps: [
    {
      title: 'Detects the miss',
      description: 'Catches a rejection or a timeout the moment it happens.',
    },
    {
      title: 'Re-tenders down the guide',
      description:
        'Moves to the next eligible rate and tenders again, repeating down the guide until a carrier accepts.',
    },
    {
      title: 'Cleans up behind it',
      description:
        'Drops the rejected carrier from the shipment, cancels the dock or pickup appointment already booked, and re-covers the load under the new carrier.',
    },
    {
      title: 'Holds rather than guesses',
      description:
        'If no eligible fallback exists inside the guardrail, the load is held for review instead of covered at any price.',
    },
  ],
  outcomes: [
    {
      title: 'Recovery before the miss reaches you',
      description:
        'A five-minute retender instead of a half-day scramble that ends in an overpaid spot load.',
    },
    {
      title: 'Your routing guide gets used the way you built it',
      description:
        'Negotiated rates get used in the order you ranked them, on every load, at every hour.',
    },
    {
      title: 'Fallbacks stop inflating spend',
      description:
        'The rate that covers the load is one you would have approved yourself.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'Validates what actually gets billed against the rate that was booked.',
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'Rebooks the dock slot the retender just invalidated.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description:
      'We get status from carriers so your team stops making check calls, and push clean updates to the people who need them.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
];

function TenderFailures() {
  return (
    <PageWrapper>
      <TenderFailuresGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Tender Failures" />
      <Footer />
    </PageWrapper>
  );
}

export default TenderFailures;
export { TenderFailures };
